import React, { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../App";
import { documentCategories } from "../data/documentCategories";
import GlassCard from "../components/GlassCard";
import { ExternalLink } from "lucide-react";
import AboutSection from "../components/AboutSection";
import "./HomePage.css";
import HomeTopAd from "../components/ads/HomeTopAd";

const HomePage = () => {
  const { language, search, setSearch } = useContext(AppContext);
  const navigate = useNavigate();
  const [filteredLinks, setFilteredLinks] = useState([]);

  const getAllLinks = () => {
    const all = [];
    documentCategories.forEach((cat) => {
      cat.links.forEach((link) =>
        all.push({
          ...link,
          categoryTitle: cat.title,
          categoryTitleEn: cat.titleEn,
          background: cat.backgroundImage,
        })
      );
      cat.subcategories?.forEach((sub) =>
        sub.links.forEach((link) =>
          all.push({
            ...link,
            categoryTitle: sub.name,
            categoryTitleEn: sub.nameEn,
            background: cat.backgroundImage,
          })
        )
      );
    });
    return all;
  };

  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      setFilteredLinks([]);
      return;
    }

    const matches = getAllLinks().filter((link) =>
      [link.name, link.nameEn, link.description, link.descriptionEn]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
    setFilteredLinks(matches);
  }, [search]);

  useEffect(() => {
    const savedPosition = sessionStorage.getItem("homeScrollPosition");
    if (savedPosition) {
      window.scrollTo(0, parseInt(savedPosition));
      sessionStorage.removeItem("homeScrollPosition");
    }

    const handleScroll = () => {
      sessionStorage.setItem("homeScrollPosition", window.scrollY.toString());
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.div
      className="relative min-h-screen flex flex-col items-center text-center px-4 pt-7"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="z-10 w-full max-w-3xl"
        initial={{ y: 25, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <input
          type="search"
          placeholder={
            language === "el"
              ? "Αναζήτηση υπηρεσίας ή εγγράφου..."
              : "Search for a service or document..."
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-full px-6 py-3 bg-white/10 text-white backdrop-blur-md border-0 focus:border-0 outline-none focus:ring-2 focus:ring-[#a3c4f3] transition-all"
        />
      </motion.div>

      {search ? (
        /* --- Search Results --- */
        <motion.div
          className="z-10 category-grid mt-12 max-w-6xl mb-20"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { delayChildren: 0.2, staggerChildren: 0.1 },
            },
          }}
        >
          {filteredLinks.length === 0 ? (
            <p className="text-gray-400 mt-12">
              {language === "el" ? "Δεν βρέθηκαν αποτελέσματα." : "No results found."}
            </p>
          ) : (
            filteredLinks.map((link, i) => (
              <GlassCard
                key={i}
                icon={ExternalLink}
                title={language === "el" ? link.name : link.nameEn}
                desc={language === "el" ? link.description : link.descriptionEn}
                background={link.background}
                onClick={() => window.open(link.url, "_blank")}
              />
            ))
          )}
        </motion.div>
      ) : (
        <>
          {/* ✅ ΠΡΟΣΘΗΚΗ ΔΙΑΦΗΜΙΣΗΣ */}
          <HomeTopAd />

          <motion.div
            className="z-10 category-grid mt-12 max-w-6xl mb-20"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { delayChildren: 0.2, staggerChildren: 0.1 },
              },
            }}
          >
            {documentCategories.map((cat) => (
              <GlassCard
                key={cat.id}
                icon={cat.icon}
                title={language === "el" ? cat.title : cat.titleEn}
                desc={
                  language === "el"
                    ? cat.description || `${cat.links.length} διαθέσιμες υπηρεσίες`
                    : cat.descriptionEn || `${cat.links.length} available services`
                }
                background={cat.backgroundImage}
                onClick={() => navigate(`/category/${cat.id}`)}
              />
            ))}
          </motion.div>

          <AboutSection />
        </>
      )}
    </motion.div>
  );
};

export default HomePage;
